import { componentRef, componentIndexForRef } from "./qptAssignments";

const newId = prefix => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyPadiGrid = (columns = 5, rows = 7) => ({
  version: 1, preset: "manual", columns, rows,
  column_widths: Array.from({ length: columns }, () => 160),
  row_heights: Array.from({ length: rows }, () => 52),
  cells: [{ id: newId("output"), row: 0, column: 0, row_span: 1, column_span: columns, text: "", dynamic_type: "recipe_output", stage_id: "independent", cell_type: "text", bold: false, align: "center" }],
});

export const gridCellAt = (grid, row, column) => (grid?.cells || []).find(cell =>
  row >= cell.row && row < cell.row + (cell.row_span || 1) && column >= cell.column && column < cell.column + (cell.column_span || 1),
);

export const gridComponentIndexes = (grid, components = []) => new Set((grid?.cells || [])
  .map(cell => cell.component_ref ? componentIndexForRef(`component:${cell.component_ref}`, components) : null)
  .filter(index => index != null));

export const unplacedGridComponentIndexes = (grid, components = []) => components
  .map((_, index) => index).filter(index => !gridComponentIndexes(grid, components).has(index));

export const normalisePadiGridComponentRefs = (grid, components = []) => !grid ? grid : ({
  ...grid,
  cells: (grid.cells || []).map(cell => {
    if (!cell.component_ref) return cell;
    const index = componentIndexForRef(`component:${cell.component_ref}`, components);
    return index == null ? cell : { ...cell, component_ref: componentRef(components[index], index).slice("component:".length) };
  }),
});

// Generated stages stay as a saved suggestion. The manager can replace this
// layout without altering source components or the generated draft.
const stageText = stage => {
  const values = [stage?.action, stage?.instruction, stage?.time && `~${stage.time}`, stage?.temperature].filter(Boolean);
  return [...new Set(values.map(value => String(value).trim()).filter(Boolean))].join(" · ");
};

const stageVessel = stage => {
  const refs = stage?.equipment_refs || [];
  return refs.find(ref => ref.equipment_type === "VESSEL")?.name || String(stage?.vessel_equipment || "").trim();
};

// Bakery / Parallel Prep is merely the initial drawing strategy. It uses the
// generic grid primitives and can therefore be replaced by future presets.
export const gridFromQptFlow = (flow, components = []) => {
  const stages = (flow?.stages || []).slice().sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const entryIndex = component => stages.findIndex(stage => (stage.input_refs || []).some(ref => componentIndexForRef(ref, components) === component));
  const componentOrder = components.map((component, index) => ({ component, index, entry: entryIndex(index) }))
    .sort((left, right) => {
      const leftRole = left.component.padi_role === "garnish" ? 1 : 0;
      const rightRole = right.component.padi_role === "garnish" ? 1 : 0;
      return leftRole - rightRole || (left.entry < 0 ? Number.MAX_SAFE_INTEGER : left.entry) - (right.entry < 0 ? Number.MAX_SAFE_INTEGER : right.entry) || left.index - right.index;
    });
  const entryStages = stages.filter(stage => (stage.input_refs || []).some(ref => componentIndexForRef(ref, components) != null));
  const firstEntryPosition = stages.findIndex(stage => entryStages.includes(stage));
  // Only actions explicitly recorded before the first ingredient entry are
  // opening prep. A later preheat/prepare instruction stays chronological
  // below the preparation area, where a manager can move it if required.
  const openingStages = (firstEntryPosition > 0 ? stages.slice(0, firstEntryPosition) : []).filter(stage => !(stage.input_refs || []).length);
  const postStages = stages.filter(stage => !entryStages.includes(stage) && !openingStages.includes(stage));
  // Keep the generated starting point compact. Later SOP stages become rows
  // rather than a wide column for every instruction; the grid stays editable.
  const columns = Math.max(3, Math.min(5, entryStages.length + 2));
  const grid = { ...emptyPadiGrid(columns, Math.max(componentOrder.length + openingStages.length + postStages.length + 2, 5)), preset: "bakery_parallel_prep",
    column_widths: [150, 125, ...Array.from({ length: Math.max(0, columns - 2) }, () => 155)] };
  const cells = grid.cells.slice();
  openingStages.forEach((stage, index) => cells.push({ id: newId("opening"), stage_id: stage.id, row: index + 1, column: 0, row_span: 1, column_span: columns, text: stageText(stage), cell_type: "opening_prep", bold: false, align: "center" }));
  componentOrder.forEach(({ component, index }, rowIndex) => {
    const row = rowIndex + openingStages.length + 1;
    const stage = stages.find(candidate => (candidate.input_refs || []).some(ref => componentIndexForRef(ref, components) === index));
    cells.push({ id: newId("component"), row, column: 0, row_span: 1, column_span: 1, text: "", component_ref: componentRef(component, index).slice("component:".length), cell_type: "component", bold: false, align: "left" });
    if (stageVessel(stage)) cells.push({ id: newId("vessel"), row, column: 1, row_span: 1, column_span: 1, text: stageVessel(stage), cell_type: "vessel", bold: true, align: "center" });
    if (stageText(stage)) cells.push({ id: newId("operation"), stage_id: stage.id, row, column: 2, row_span: 1, column_span: Math.max(1, columns - 2), text: stageText(stage), cell_type: "operation", bold: false, align: "center" });
  });
  postStages.forEach((stage, index) => cells.push({ id: newId("post"), stage_id: stage.id, row: openingStages.length + componentOrder.length + 1 + index, column: 0, row_span: 1, column_span: columns, text: stageText(stage), cell_type: "post_process", bold: false, align: "center" }));
  return { ...grid, cells, generated_draft: { preset: "bakery_parallel_prep", flow: JSON.parse(JSON.stringify(flow || { stages: [] })), generated_at: new Date().toISOString() } };
};

export const updatePadiGridCell = (grid, row, column, patch) => {
  const current = gridCellAt(grid, row, column);
  const cells = (grid?.cells || []).filter(cell => cell !== current);
  const base = current || { id: newId("cell"), row, column, row_span: 1, column_span: 1, text: "", bold: false, align: "left" };
  return { ...grid, cells: [...cells, { ...base, ...patch }] };
};

const mergeKind=c=>['measuring','processing','transfer','storage','service'].includes(c.cell_type)?c.cell_type:['vessel','equipment'].includes(c.cell_type)?'measuring':'processing';
export const padiMergeIssue = (grid, selected=[]) => {
  const cells=[...new Set(selected)];
  const protectedCell=c=>c.component_ref!=null||c.dynamic_type||(c.merged_cells||[]).some(protectedCell);
  if(cells.length<2)return 'Select at least two cells to merge.';
  if(cells.some(protectedCell))return 'Ingredient and Recipe output cells cannot merge. Select only layout or vessel cells.';
  const refs=cells.map(c=>c.equipment_ref).filter(Boolean);
  if(new Set(refs.map(ref=>ref.equipment_id)).size>1)return `Cannot merge different Equipment: ${[...new Set(refs.map(ref=>ref.name||ref.equipment_id))].join(' and ')}. Choose the same Equipment deliberately first; no reference was changed.`;
  if(new Set(cells.map(mergeKind)).size!==1)return 'Selected cells have different semantic types. Choose the same type before merging.';
  const top=Math.min(...cells.map(c=>c.row)),left=Math.min(...cells.map(c=>c.column));
  const bottom=Math.max(...cells.map(c=>c.row+(c.row_span||1))),right=Math.max(...cells.map(c=>c.column+(c.column_span||1)));
  if((grid.cells||[]).some(c=>!cells.includes(c)&&c.row<bottom&&c.row+(c.row_span||1)>top&&c.column<right&&c.column+(c.column_span||1)>left))return 'The selected rectangle contains unselected cells. Select every included cell; Ingredient boundaries cannot be absorbed.';
  const positions=new Set();for(const c of cells)for(let r=c.row;r<c.row+(c.row_span||1);r++)for(let col=c.column;col<c.column+(c.column_span||1);col++){const key=`${r}:${col}`;if(positions.has(key))return 'Selected cells have overlapping spans. Correct the overlap before merging.';positions.add(key);}
  if(positions.size!==(bottom-top)*(right-left))return 'Selection has gaps. Select a complete adjacent rectangle to merge.';
  return '';
};
export const mergePadiLayoutCells = (grid, selected = []) => {
  const cells=[...new Set(selected)];if(padiMergeIssue(grid,cells))return grid;
  const refs=cells.map(c=>c.equipment_ref).filter(Boolean);
  const top=Math.min(...cells.map(c=>c.row)),left=Math.min(...cells.map(c=>c.column));
  const bottom=Math.max(...cells.map(c=>c.row+(c.row_span||1))),right=Math.max(...cells.map(c=>c.column+(c.column_span||1)));
  const ordered=[...cells].sort((a,b)=>a.row-b.row||a.column-b.column);
  return {...grid,cells:[...grid.cells.filter(c=>!cells.includes(c)),{bold:ordered[0].bold,align:ordered[0].align,id:newId('merge'),row:top,column:left,row_span:bottom-top,column_span:right-left,text:ordered.map(c=>c.text).filter(Boolean).join('\n'),cell_type:mergeKind(ordered[0]),equipment_ref:refs[0]||null,merged_cells:JSON.parse(JSON.stringify(ordered))}]};
};

export const mergePadiGridCells = (grid, positions = []) => {
  if (!positions.length) return grid;
  const rows = positions.map(position => position.row); const columns = positions.map(position => position.column);
  const top = Math.min(...rows), bottom = Math.max(...rows), left = Math.min(...columns), right = Math.max(...columns);
  if ((bottom - top + 1) * (right - left + 1) !== positions.length) return grid;
  let materialized = grid;
  positions.forEach(({row,column}) => { if (!gridCellAt(materialized,row,column)) materialized=updatePadiGridCell(materialized,row,column,{}); });
  const selected = positions.map(position => gridCellAt(materialized, position.row, position.column));
  const merged=mergePadiLayoutCells(materialized,selected);
  return merged===materialized ? grid : merged;
};

export const unmergePadiGridCell = (grid, row, column) => {
  const cell = gridCellAt(grid, row, column);
  if(cell?.merged_cells?.length){
    if((cell.text||'')!==cell.merged_cells.map(c=>c.text).filter(Boolean).join('\n'))return grid;
    return {...grid,cells:[...grid.cells.filter(c=>c!==cell),...JSON.parse(JSON.stringify(cell.merged_cells))]};
  }
  if (!cell || ((cell.row_span || 1) === 1 && (cell.column_span || 1) === 1)) return grid;
  return { ...grid, cells: (grid.cells || []).map(item => item === cell ? { ...item, row_span: 1, column_span: 1 } : item) };
};

export const insertPadiGridRow = grid => ({ ...grid, rows: grid.rows + 1, row_heights: [...(grid.row_heights || []), 52] });
export const insertPadiGridColumn = grid => ({ ...grid, columns: grid.columns + 1, column_widths: [...(grid.column_widths || []), 160] });

export const removePadiGridRow = (grid, row) => {
  if (grid.rows <= 1) return grid;
  const cells = (grid.cells || []).flatMap(cell => {
    const bottom = cell.row + (cell.row_span || 1) - 1;
    if (row < cell.row) return [{ ...cell, row: cell.row - 1 }];
    if (row > bottom) return [cell];
    if ((cell.row_span || 1) > 1) return [{ ...cell, row_span: cell.row_span - 1, row: Math.min(cell.row, row) }];
    return [];
  });
  return { ...grid, rows: grid.rows - 1, row_heights: (grid.row_heights || []).filter((_, index) => index !== row), cells };
};

export const removePadiGridColumn = (grid, column) => {
  if (grid.columns <= 1) return grid;
  const cells = (grid.cells || []).flatMap(cell => {
    const right = cell.column + (cell.column_span || 1) - 1;
    if (column < cell.column) return [{ ...cell, column: cell.column - 1 }];
    if (column > right) return [cell];
    if ((cell.column_span || 1) > 1) return [{ ...cell, column_span: cell.column_span - 1, column: Math.min(cell.column, column) }];
    return [];
  });
  return { ...grid, columns: grid.columns - 1, column_widths: (grid.column_widths || []).filter((_, index) => index !== column), cells };
};

export const resizePadiGridColumn = (grid, column, width) => ({ ...grid, column_widths: Array.from({ length: grid.columns }, (_, index) => index === column ? Math.max(80, Number(width) || 160) : (grid.column_widths || [])[index] || 160) });
export const resizePadiGridRow = (grid, row, height) => ({ ...grid, row_heights: Array.from({ length: grid.rows }, (_, index) => index === row ? Math.max(38, Number(height) || 52) : (grid.row_heights || [])[index] || 52) });

export const movePadiGridCell = (grid, row, column, rowDelta, columnDelta = 0) => {
  const cell = gridCellAt(grid, row, column);
  if (!cell) return grid;
  const nextRow = Math.max(0, Math.min(grid.rows - (cell.row_span || 1), cell.row + rowDelta));
  const nextColumn = Math.max(0, Math.min(grid.columns - (cell.column_span || 1), cell.column + columnDelta));
  const occupied = gridCellAt(grid, nextRow, nextColumn);
  if (occupied && occupied !== cell) return grid;
  return { ...grid, cells: (grid.cells || []).map(item => item === cell ? { ...item, row: nextRow, column: nextColumn } : item) };
};

// Width metadata cannot create CSS tracks. Preserve every declared column
// and every saved span; expose legacy span expansion equally in both views.
export function padiGridGeometry(grid) {
  if(!grid)return grid;
  const all=cells=>(cells||[]).flatMap(c=>[c,...all(c.merged_cells)]),cells=all(grid.cells);
  const columns=Math.max(Number(grid.columns)||1,...cells.map(c=>c.column+(c.column_span||1)));
  const rows=Math.max(Number(grid.rows)||1,...cells.map(c=>c.row+(c.row_span||1)));
  return {...grid,columns,rows,column_widths:Array.from({length:columns},(_,i)=>Number(grid.column_widths?.[i])>0?Number(grid.column_widths[i]):160),...(grid.row_heights?.length?{row_heights:Array.from({length:rows},(_,i)=>Number(grid.row_heights?.[i])>0?Number(grid.row_heights[i]):52)}:{})};
}
